import { memo, useCallback, useEffect, useState } from "react";

import {
  createOfficeEmailAPI,
  getOfficeEmailAPI,
  getOfficeEmailsAPI,
  updateOfficeEmailAPI,
} from "services/api/offices/api";
import { OfficeEmailEditFormValues, OfficeEmailRow } from "services/api/offices/types";
import { initialPagination } from "ui/components/Table/consts";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialOfficeEmailEditForm } from "services/api/offices/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";

import { OfficeEmailEditProps } from "./types";
import { OFFICE_EMAIL_EDIT_FORM_SCHEMA } from "./validation";

import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { CheckBox } from "ui/components/CheckBox";
import { TextArea } from "ui/components/TextField/TextArea";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";

const OfficeEmailEditCard: React.FC<OfficeEmailEditProps> = (props) => {
  const { emailId, officeId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingEmails, setExistingEmails] = useState<OfficeEmailRow[]>([]);

  const [formValues, setFormValues] = useState<OfficeEmailEditFormValues>(
    initialOfficeEmailEditForm
  );

  useEffect(() => {
    if (!show) return;
    fetchExistingEmails();
    fetchEmail();
  }, [emailId, show]);

  const fetchExistingEmails = async () => {
    try {
      const result = await getOfficeEmailsAPI({ ...initialPagination, pageSize: 100 }, officeId);
      setExistingEmails(result.data);
    } catch {
      setExistingEmails([]);
    }
  };

  const fetchEmail = async () => {
    if (!emailId) {
      setFormValues(initialOfficeEmailEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getOfficeEmailAPI(officeId, emailId);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, OFFICE_EMAIL_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    const emailExists = existingEmails.some(
      (email) =>
        email.emailAddress.toLowerCase() === formValues.emailAddress?.toLowerCase() &&
        email.id !== emailId
    );

    if (emailExists) {
      setErrors((prev) => ({
        ...prev,
        emailAddress: "Email adresa već postoji",
      }));
      return;
    }

    if (formValues.primary) {
      const existingPrimaryEmail = existingEmails.find(
        (email) => email.primary && email.id !== emailId
      );

      if (existingPrimaryEmail) {
        setErrors((prev) => ({
          ...prev,
          primary: `Primarni email već postoji: ${existingPrimaryEmail.emailAddress}`,
        }));
        return;
      }
    }

    setIsLoading(true);

    try {
      let result;
      if (!!emailId) {
        result = await updateOfficeEmailAPI(officeId, emailId, formValues);
      } else {
        result = await createOfficeEmailAPI(officeId, formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, primary: !old.primary }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose,
    });
  };

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={emailId ? "Ažuriranje email adrese" : "Unos email adrese"}
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="">
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextField
                        type="text"
                        name="emailAddress"
                        label="Email adresa"
                        placeholder="Unesite email adresu"
                        onChange={handleTextFieldChange}
                        error={errors.emailAddress}
                        value={formValues.emailAddress}
                        required
                        forceShowError
                        endIcon=""
                      ></TextField>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextArea
                        name="description"
                        label="Opis"
                        placeholder="Unesite opis"
                        onChange={handleTextFieldChange}
                        error={errors.description}
                        value={formValues.description}
                        required
                        endIcon=""
                      ></TextArea>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <CheckBox
                        name="primary"
                        onChange={handleCheckBoxChange}
                        checked={Boolean(formValues.primary)}
                        label="Primarni"
                        error={errors.primary}
                      ></CheckBox>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button
            variant="outlined"
            children={t("admin.close")}
            onClick={handleCloseModal}
          />
          <Button
            variant="primary"
            children={t("admin.save")}
            onClick={handleFormSubmit}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(OfficeEmailEditCard);
